import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Spectrogram from 'spectrogram';
import { Button } from './ui/button';

interface SpectrogramDisplayProps {
  file: File;
  beats?: number[];
  midpoints?: number[];
}

export interface SpectrogramDisplayHandle {
  stop: () => void;
}

const SpectrogramDisplay = forwardRef<
  SpectrogramDisplayHandle,
  SpectrogramDisplayProps
>(({ file, beats = [], midpoints = [] }, ref) => {
  const AUDIO_DELAY = 2; // seconds
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<SVGSVGElement>(null);
  const cursorRef = useRef<SVGLineElement | null>(null);
  const beatLinesRef = useRef<{ line: SVGLineElement; time: number }[]>([]);
  const midLinesRef = useRef<{ line: SVGLineElement; time: number }[]>([]);
  const hBarRef = useRef<SVGLineElement | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const pxPerSecRef = useRef(0);
  const whiteLineXRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animRef = useRef<number>();
  const durationRef = useRef(0);
  const spectroRef = useRef<any>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const [position, setPosition] = useState(0); // percent 0-100
  const [playing, setPlaying] = useState(false);
  const playStartRef = useRef(0);
  const spectroStartRef = useRef(0);
  const audioStartTimeoutRef = useRef<number | null>(null);
  const playingRef = useRef(false);
  const flashBar = useCallback(() => {
    const bar = hBarRef.current;
    if (!bar) return;
    bar.setAttribute('stroke', 'blue');
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = window.setTimeout(() => {
      bar.setAttribute('stroke', 'white');
    }, 100);
  }, []);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  const drawOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    const duration = durationRef.current;
    if (!canvas || !overlay || !duration) return;

    overlay.setAttribute('viewBox', `0 0 ${canvas.width} ${canvas.height}`);
    while (overlay.firstChild) overlay.removeChild(overlay.firstChild);
    beatLinesRef.current = [];
    midLinesRef.current = [];
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }

    const makeLine = (color: string, timeSec: number, y1: number, y2: number) => {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('y1', y1.toString());
      line.setAttribute('y2', y2.toString());
      line.setAttribute('stroke', color);
      line.setAttribute('stroke-width', '1');
      overlay.appendChild(line);
      return { line, time: timeSec };
    };

    const halfHeight = canvas.height / 2;
    beats.forEach((t) =>
      beatLinesRef.current.push(
        makeLine('blue', t / 1000, halfHeight, canvas.height),
      ),
    );
    midpoints.forEach((t) =>
      midLinesRef.current.push(makeLine('yellow', t / 1000, 0, halfHeight)),
    );

    const hBar = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    hBar.setAttribute('x1', '0');
    hBar.setAttribute('x2', canvas.width.toString());
    const y = (canvas.height / 2).toString();
    hBar.setAttribute('y1', y);
    hBar.setAttribute('y2', y);
    hBar.setAttribute('stroke', 'white');
    hBar.setAttribute('stroke-width', '10');
    overlay.appendChild(hBar);
    hBarRef.current = hBar;

    const cursor = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    cursor.setAttribute('y1', '0');
    cursor.setAttribute('y2', canvas.height.toString());
    cursor.setAttribute('stroke', 'white');
    cursor.setAttribute('stroke-width', '1');
    overlay.appendChild(cursor);
    cursorRef.current = cursor;
    whiteLineXRef.current = canvas.width - pxPerSecRef.current * AUDIO_DELAY;
    cursor.setAttribute('x1', whiteLineXRef.current.toString());
    cursor.setAttribute('x2', whiteLineXRef.current.toString());
  }, [beats, midpoints]);

  const updateOverlayPosition = useCallback((spectroTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pxPerSec = pxPerSecRef.current;
    const whiteX = whiteLineXRef.current;
    let overlap = false;
    const update = (arr: { line: SVGLineElement; time: number }[]) => {
      arr.forEach(({ line, time }) => {
        const x = whiteX + (time - spectroTime + AUDIO_DELAY) * pxPerSec;
        if (x >= 0 && x <= canvas.width) {
          line.setAttribute('x1', x.toString());
          line.setAttribute('x2', x.toString());
          line.style.display = '';
          if (Math.abs(x - whiteX) < 1) overlap = true;
        } else {
          line.style.display = 'none';
        }
      });
    };
    update(beatLinesRef.current);
    update(midLinesRef.current);
    if (overlap) flashBar();
  }, [flashBar, AUDIO_DELAY]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const render = async () => {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
      durationRef.current = audioBuffer.duration;
      pxPerSecRef.current = audioBuffer.sampleRate / 2048;
      const spectro = Spectrogram(canvas, { audio: { enable: false } });
      spectro.connectSource(audioBuffer, audioCtx);
      spectro.start(0);
      spectro.pause();
      spectroRef.current = spectro;
      audioCtxRef.current = audioCtx;
      audioBufferRef.current = audioBuffer;

      const url = URL.createObjectURL(file);
      const audio = new Audio(url);
      audio.loop = true;
      audioRef.current = audio;
      drawOverlay();
      updateOverlayPosition(0);
    };

    void render();

    return () => {
      spectroRef.current?.stop?.();
      audioCtxRef.current?.close();
      cancelAnimationFrame(animRef.current);
      if (audioStartTimeoutRef.current) {
        clearTimeout(audioStartTimeoutRef.current);
        audioStartTimeoutRef.current = null;
      }
      audioRef.current?.pause();
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    };
  }, [file, drawOverlay, updateOverlayPosition]);

  useEffect(() => {
    drawOverlay();
    updateOverlayPosition(spectroStartRef.current);
  }, [drawOverlay, updateOverlayPosition]);

  const syncSpectrogram = (time: number, shouldPlay: boolean) => {
    const spectro = spectroRef.current;
    const audioCtx = audioCtxRef.current;
    const audioBuffer = audioBufferRef.current;
    if (!spectro || !audioCtx || !audioBuffer) return;
    spectro.clear();
    spectro.connectSource(audioBuffer, audioCtx);
    spectro.start(time);
    if (!shouldPlay) {
      setTimeout(() => spectro.pause(), 100);
    }
  };

  const updateProgress = () => {
    const duration = durationRef.current;
    if (!duration) return;
    const now = performance.now();
    let spectroTime = spectroStartRef.current + (now - playStartRef.current) / 1000;
    if (spectroTime >= duration) {
      stop();
      return;
    }
    setPosition((spectroTime / duration) * 100);
    updateOverlayPosition(spectroTime);
    if (playingRef.current) {
      animRef.current = requestAnimationFrame(updateProgress);
    }
  };

  const play = () => {
    const audio = audioRef.current;
    const duration = durationRef.current;
    if (!audio || !duration) return;
    const spectroTime = 0;
    spectroStartRef.current = spectroTime;
    playStartRef.current = performance.now();
    setPosition(0);
    syncSpectrogram(spectroTime, true);
    updateOverlayPosition(spectroTime);
    setPlaying(true);
    if (audioStartTimeoutRef.current) {
      clearTimeout(audioStartTimeoutRef.current);
    }
    audioStartTimeoutRef.current = window.setTimeout(() => {
      audio.currentTime = Math.max(0, spectroStartRef.current);
      void audio.play();
    }, AUDIO_DELAY * 1000);
    animRef.current = requestAnimationFrame(updateProgress);
  };

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audioStartTimeoutRef.current) {
      clearTimeout(audioStartTimeoutRef.current);
      audioStartTimeoutRef.current = null;
    }
    audio?.pause();
    spectroRef.current?.pause?.();
    setPlaying(false);
    cancelAnimationFrame(animRef.current);
    spectroStartRef.current = 0;
    playStartRef.current = 0;
    setPosition(0);
    if (audio) audio.currentTime = 0;
    syncSpectrogram(0, false);
    updateOverlayPosition(0);
  }, [updateOverlayPosition]);

  useImperativeHandle(ref, () => ({ stop }));

  const handlePlayClick = () => {
    if (playing) stop();
    else play();
  };

  return (
    <div className="mt-2 w-full">
      <div className="relative">
        <canvas ref={canvasRef} width={600} height={200} className="w-full border" />
        <svg
          ref={overlayRef}
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
      </div>
      <div className="mt-2 flex items-center">
        <input
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={position}
          className="flex-grow pointer-events-none"
          readOnly
        />
        <Button size="sm" className="ml-2" onClick={handlePlayClick}>
          {playing ? 'Stop' : 'Play'}
        </Button>
      </div>
    </div>
  );
});

export default SpectrogramDisplay;
